<template>
  <div class="container-fluid plan-detail-page">
    <div class="plan-detail-header pb-3">
      <div class="d-flex align-items-center mb-1">
        <router-link :to="backRoute" class="text-secondary text-sm me-2">
          <i class="fas fa-arrow-left me-1"></i> Plan
        </router-link>
      </div>
      <h4 class="mb-0 font-weight-bold">{{ testPlan.subProjectName || $route.params.id }}</h4>
      <p class="text-secondary text-sm mb-0">{{ testPlan.projectName || '' }}</p>
    </div>

    <!-- Tabs -->
    <div class="row pb-2 plan-detail-main">
      <div class="col-12 plan-detail-col">
        <div class="card plan-detail-card">
          <div class="card-header pb-0">
            <div class="tab-shell" id="detailTabs" role="tablist" aria-label="Test plan detail tabs">
              <button
                v-for="tab in visibleTabs"
                :key="tab.tabId"
                class="tab-item"
                :class="{ active: activeTab === tab.tabId }"
                @click="onTabClick(tab.tabId)"
                type="button"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2 plan-detail-tab-body">
            <!-- Scope Tab -->
            <scope-tab
              v-if="activeTab === 'scope'"
              ref="scopeTab"
              :project-plan-id="$route.params.id"
              :sub-project-id="testPlan.subProjectId"
              :sub-project-name="testPlan.subProjectName || ''"
              :initial-scope="testPlan.scope || ''"
              :initial-jira-links="parsedJiraLinks"
              @jira-links-updated="onJiraLinksUpdated"
            />

            <!-- Task Tab -->
            <test-plan-tab v-if="activeTab === 'testplan'" @plans-updated="availablePlans = $event" />

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TestPlanTab from "./components/tabs/TestPlanTab.vue";
import ScopeTab from "./components/tabs/ScopeTab.vue";
import "@/assets/css/plan-tab-scroll.css";
import { projectPlansApi } from "@/services/backendApi";
import { ensureFeatureAccess } from "@/utils/featureAccess";
import {
  PLAN_DETAIL_TABS,
  defaultPlanTabId,
  filterPlanDetailTabs,
  isPlanTabAllowed,
} from "@/utils/planFeatureAccess";
import { planListRouteFromDetailQuery } from "@/utils/planNav";

export default {
  name: "test-plan-detail",
  components: {
    TestPlanTab,
    ScopeTab,
  },
  data() {
    return {
      activeTab: "scope",
      testPlan: {},
      availablePlans: [
        {
          phase: "Phase 1 — Core Features",
          overallDueDate: "2026-03-14",
          items: [
            { feature: "Login", notes: "Happy Path", qa: "John D.", testCases: 8, startDate: "01/03/26", dueDate: "07/03/26", status: "Done" },
            { feature: "Login", notes: "Negative Case", qa: "Jane S.", testCases: 6, startDate: "01/03/26", dueDate: "07/03/26", status: "Done" },
            { feature: "Dashboard", notes: "UI Verification", qa: "John D.", testCases: 10, startDate: "08/03/26", dueDate: "14/03/26", status: "In Progress" },
          ],
        },
        {
          phase: "Phase 2 — Advanced Features",
          overallDueDate: "2026-03-28",
          items: [
            { feature: "Profile", notes: "Edit Profile", qa: "Jane S.", testCases: 12, startDate: "15/03/26", dueDate: "21/03/26", status: "Pending" },
            { feature: "Responsive", notes: "Mobile", qa: "Mike R.", testCases: 9, startDate: "15/03/26", dueDate: "21/03/26", status: "Pending" },
            { feature: "TC-006 - User profile update", notes: "Profile / Edit Profile", qa: "Jane S.", testCases: 1, startDate: "15/03/26", dueDate: "21/03/26", status: "Pending" },
          ],
        },
      ],
    };
  },
  computed: {
    visibleTabs() {
      const tabs = filterPlanDetailTabs(this.$store.state.featureAccess)
        .filter((tab) => tab.tabId === "scope" || tab.tabId === "testplan");
      if (tabs.length) return tabs;
      return PLAN_DETAIL_TABS.filter((tab) => tab.tabId === "scope" || tab.tabId === "testplan");
    },
    backRoute() {
      const from = String(this.$route.query?.from || "");
      if (from === "qa") {
        const project = String(this.$route.query?.project || "").trim();
        const subProject = String(this.$route.query?.subProject || "").trim();
        const folder = String(this.$route.query?.folder || this.$route.query?.set || "").trim();
        const query = {};
        if (project) query.project = project;
        if (subProject) query.subProject = subProject;
        if (folder) query.folder = folder;
        return Object.keys(query).length
          ? { name: "QA Test Case", query }
          : { name: "QA Test Case" };
      }
      return planListRouteFromDetailQuery(this.$route.query) || { name: "Plan Team All" };
    },
    parsedJiraLinks() {
      const raw = this.testPlan.projectLink;
      if (!raw) return [];
      try { const arr = JSON.parse(raw.trim()); return Array.isArray(arr) ? arr : []; } catch (_) { return []; }
    },
  },
  async created() {
    try {
      this.testPlan = await projectPlansApi.getById(this.$route.params.id);
    } catch (e) {
      console.error("load project plan error:", e);
    }
    try {
      await ensureFeatureAccess(this.$store);
    } catch (e) {
      console.error("feature access error:", e);
    }
    const access = this.$store.state.featureAccess;
    const allowedIds = this.visibleTabs.map((tab) => tab.tabId);
    const requestedTab = String(this.$route.query.tab || "").trim();
    if (requestedTab && allowedIds.includes(requestedTab) && isPlanTabAllowed(requestedTab, access)) {
      this.activeTab = requestedTab;
    } else if (allowedIds.includes("scope")) {
      this.activeTab = "scope";
    } else {
      this.activeTab = allowedIds[0] || defaultPlanTabId(access);
    }
  },
  mounted() {
    document.documentElement.classList.add("plan-detail-scroll-lock");
  },
  beforeUnmount() {
    document.documentElement.classList.remove("plan-detail-scroll-lock");
  },
  methods: {
    async onTabClick(tabId) {
      if (tabId === this.activeTab) return;
      if (this.activeTab === "scope" && tabId !== "scope") {
        const scopeTab = this.$refs.scopeTab;
        if (scopeTab?.isScopeDirty?.()) {
          const allowed = await scopeTab.requestTabLeave();
          if (!allowed) return;
        }
      }
      this.activeTab = tabId;
    },
    onJiraLinksUpdated(links) {
      // Reflect update into testPlan.projectLink so other tabs see updated data
      this.testPlan = { ...this.testPlan, projectLink: JSON.stringify(links) };
    },
  },
};
</script>

<style scoped>
.plan-detail-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.plan-detail-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plan-detail-col {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.plan-detail-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plan-detail-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plan-detail-header {
  background-color: var(--surface-ground, #f4f6f9);
}

.card-header.pb-0 {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}
.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}
</style>
